import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

interface ChatTarget {
  id: number;
  name: string;
  avatar_url?: string | null;
  kind: 'dm' | 'group' | 'favorites';
}

interface ConversationResponse {
  user_id: number;
  username: string;
  avatar_url: string | null;
}

interface ForwardModalProps {
  messageId: number;
  onClose: () => void;
}

const ForwardModal: React.FC<ForwardModalProps> = ({ messageId, onClose }) => {
  const [targets, setTargets] = useState<ChatTarget[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    loadTargets();
  }, []);

  const loadTargets = async () => {
    try {
      const [dmRes, groupRes] = await Promise.all([
        api.get('/messages/'),
        api.get('/messages/group/list')
      ]);

      const dmByUserId = new Map<number, ChatTarget>();
      (dmRes.data as ConversationResponse[]).forEach((conversation) => {
        if (conversation.user_id > 0 && !dmByUserId.has(conversation.user_id)) {
          dmByUserId.set(conversation.user_id, {
            id: conversation.user_id,
            name: conversation.username,
            avatar_url: conversation.avatar_url,
            kind: 'dm',
          });
        }
      });

      const groups: ChatTarget[] = groupRes.data.map((g: any) => ({
        id: g.id,
        name: g.name,
        kind: 'group'
      }));

      setTargets([
        { id: 0, name: 'Избранное', kind: 'favorites' },
        ...groups,
        ...dmByUserId.values(),
      ]);
    } catch {}
  };

  const forward = async (target: ChatTarget) => {
    setLoading(true);
    try {
      const receiverId = target.kind === 'group' ? -target.id : target.id;
      await api.post(`/messages/${messageId}/forward`, { to_receiver_id: receiverId });
      onClose();
    } catch {}
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1a1d24] border border-gray-700 rounded-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-white font-semibold">Переслать сообщение</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">×</button>
        </div>

        <div className="p-2 max-h-80 overflow-y-auto">
          {targets.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-6">Нет чатов для пересылки</p>
          )}
          {targets.map(t => (
            <button
              key={`${t.kind}-${t.id}`}
              onClick={() => forward(t)}
              disabled={loading}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition text-left disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-full bg-gray-700 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {t.avatar_url ? (
                  <img src={t.avatar_url} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg">{t.kind === 'group' ? '👥' : t.kind === 'favorites' ? '⭐' : '👤'}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{t.name}</p>
                <p className="text-gray-500 text-xs">
                  {t.kind === 'group' ? 'Группа' : t.kind === 'favorites' ? 'Сохранённые сообщения' : 'Личные сообщения'}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ForwardModal;
